import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormikContext } from 'formik'; // Import Formik context
import _ from 'lodash';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import createCustomHighlightPlugin from '../plugins/customHighlightPlugin';
import { searchPlugin, SearchIcon } from '@react-pdf-viewer/search';
import SearchSidebar from '../pages/subPages/SearchSidebar';
import { selectFullBookTreeData, uploadFileToS3 } from '../slices/storyEditorSlice';
import { fetchSignedUrlByType } from '../utils/fetchSignedUrlByFolderV2';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import { resetUserMessages } from '../slices/sseSlice';

const PdfViewer = () => {
  const dispatch = useDispatch();

  const { submitForm, setFieldValue, values } = useFormikContext(); // Access Formik's submitForm and setFieldValue
  const userProfile = useSelector(state => state.user.profile);
  const hasFullBook = useSelector(selectFullBookTreeData);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [isReadyCount, setIsReadyCount] = useState(0);

  const resetSearchStateRef = useRef(null);
  const searchPluginInstance = searchPlugin();
  const customHighlightPluginInstance = createCustomHighlightPlugin();

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      {
        content: (<SearchSidebar searchPluginInstance={defaultLayoutPluginInstance.toolbarPluginInstance.searchPluginInstance} />),
        icon: <SearchIcon />,
        title: 'Search',
      },
    ].concat(defaultTabs[0]),
  });

  const handleFileChange = useCallback(async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && getFileExt(selectedFile.name).toLowerCase() === 'pdf') {
      const url = URL.createObjectURL(selectedFile);
      // setFileUrl(url);

      try {
        const sourcePdfSignedUrl = await fetchSignedUrlByType({
          requestType: 'BOOK_ELEMENTS_SOURCE_PDF',
          userId: userProfile.data.userId,
          bookUuid: `${hasFullBook.bookTree.uuid}`,
          fileName: selectedFile.name
        });

        await uploadFileToS3(sourcePdfSignedUrl, selectedFile);
        console.log('File uploaded successfully');

        // Trigger Formik's setFieldValue to update the form state with the file URL
        setFieldValue('contentUrl', url);  // Update form field

        // Trigger Formik's submit form
        submitForm();  // Programmatically trigger form submission
      } catch (error) {
        console.error('Error during file processing:', error);
      }

      if (resetSearchStateRef.current) {
        resetSearchStateRef.current();
      }
    } else {
      console.log('Please select a PDF file.');
    }
  }, [userProfile.data.userId, hasFullBook, submitForm, setFieldValue]);

  const getFileExt = (fileName) => {
    return fileName.split('.').pop();
  };

  useEffect(() => {
    if (isViewerReady) {
      const inputElement = document.querySelector('.rpv-open__input');
      if (inputElement) {
        inputElement.addEventListener('change', handleFileChange);
        return () => {
          inputElement.removeEventListener('change', handleFileChange);
        };
      }
    }
  }, [isViewerReady, handleFileChange]);

  useEffect(() => {
    dispatch(resetUserMessages(userProfile.data.userId));
  }, [userProfile.data.userId, dispatch]);

  const handleDocumentLoad = (document) => {
    console.log('Document loaded:', document);
    setIsViewerReady(true);
    setIsReadyCount(isReadyCount + 1);
  };


  return (
    <div style={{ height: '70vh', position: 'relative' }}>
      {/*
        Load the worker from our own /public folder so its version is always
        in lockstep with the installed pdfjs-dist API version. A hard-coded
        CDN URL here will drift on the next `npm install` and produce an
        "API/Worker version mismatch" runtime error.
        See: docs/operations/pdfjs-worker.md.
      */}
      <Worker workerUrl={`${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`}>
        <div style={{ height: "70vh" }}>
          <Viewer
            fileUrl={values.contentUrl}
            plugins={[
              customHighlightPluginInstance,
              defaultLayoutPluginInstance,
              searchPluginInstance
            ]}
            initialPage={1}
            defaultScale={1}
            onDocumentLoad={handleDocumentLoad}
          />
        </div>
      </Worker>
    </div>
  );
};

export default PdfViewer;
